import type { AcaoOtimizacaoResposta } from '@baiocchi/shared'
import {
  Cartao,
  Dica,
  EstadoVazio,
  Etiqueta,
  TabelaDados,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarDataHora } from '../../utilitarios/formatar'
import { formatarMetrica } from './componentes'

/**
 * TELA-META-09 — Histórico de otimizações (§17).
 *
 * ## Por que esta tela é a mais importante a longo prazo
 *
 * O §17 diz: *"isso será extremamente importante para sabermos se a IA realmente melhora
 * performance."* Sem histórico com antes/depois e motivo, não há como responder isso — as
 * decisões viram folclore ("a gente pausou aquele anúncio e melhorou", sem número).
 *
 * A tabela mostra o registro completo: quem, quando, qual conta, qual alvo, qual ação, o
 * valor anterior, o novo, o motivo escrito e o resultado posterior.
 *
 * ## Tentativas que falharam também aparecem
 *
 * Ação com status `FALHOU` fica na lista, com a mensagem da Meta. Esconder a falha faria o
 * histórico dizer que só houve sucessos — e o gestor que tentou pausar um anúncio e não
 * conseguiu não teria onde ver o motivo.
 *
 * ## `resultadoPosterior` ainda é vazio, e a tela diz isso
 *
 * A coluna existe porque a estrutura de dados existe; a avaliação automática do efeito é
 * trabalho da Fase 3. Mostrar a coluna com "—" e a explicação na dica é mais honesto do que
 * omitir e deixar parecer que a pergunta do §17 já está respondida.
 */
export function PaginaMetaAdsOtimizacoes() {
  const { filtros } = useFiltrosUrl()
  const parametros = { contaId: filtros.contaId }

  const consulta = useQuery({
    queryKey: chaves.metaAds.otimizacoes(parametros),
    queryFn: ({ signal }) => metaAdsApi.otimizacoes(parametros, signal),
    staleTime: 30_000,
  })

  return (
    <>
      <CabecalhoPagina
        titulo="Histórico de otimizações"
        descricao="Toda alteração enviada à Meta, com autor, motivo e antes/depois."
      />

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}

      <Cartao semPadding>
        <TabelaDados<AcaoOtimizacaoResposta>
          colunas={COLUNAS}
          itens={consulta.data?.dados ?? []}
          chaveDe={(acao) => acao.id}
          carregando={consulta.isLoading}
          vazio={
            <EstadoVazio
              titulo="Nenhuma otimização registrada"
              descricao="Nada foi alterado na Meta a partir do CRM. As recomendações continuam podendo ser aplicadas manualmente no Gerenciador de Anúncios."
            />
          }
          cartaoMobile={(acao) => <CartaoAcao acao={acao} />}
        />
      </Cartao>
    </>
  )
}

const COLUNAS: ReadonlyArray<ColunaTabela<AcaoOtimizacaoResposta>> = [
  {
    chave: 'solicitadaEm',
    cabecalho: 'Quando',
    classe: 'whitespace-nowrap',
    celula: (acao) => (
      <div>
        <p className="text-sm">{formatarDataHora(acao.solicitadaEm)}</p>
        <p className="text-xs text-conteudo-fraco">{acao.usuarioNome}</p>
      </div>
    ),
  },
  {
    chave: 'alvo',
    cabecalho: 'Alvo',
    classe: 'min-w-[14rem]',
    celula: (acao) => (
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-conteudo">{acao.nomeAlvo}</p>
        <p className="text-xs text-conteudo-fraco">
          {ROTULO_NIVEL[acao.nivel]} · {acao.contaNome}
        </p>
      </div>
    ),
  },
  {
    chave: 'acao',
    cabecalho: 'Ação',
    celula: (acao) => <Etiqueta tom="neutro" tamanho="sm">{ROTULO_ACAO[acao.acao]}</Etiqueta>,
  },
  {
    chave: 'mudanca',
    cabecalho: 'Antes → Depois',
    ocultarAte: 'md',
    celula: (acao) => (
      <span className="text-sm tabular-nums">{descreverMudanca(acao)}</span>
    ),
  },
  {
    chave: 'status',
    cabecalho: 'Situação',
    celula: (acao) =>
      acao.status === 'FALHOU' && acao.erroMensagem !== null ? (
        <Dica texto={acao.erroMensagem}>
          <Etiqueta tom="perigo" tamanho="sm">
            Falhou
          </Etiqueta>
        </Dica>
      ) : (
        <Etiqueta tom={TOM_STATUS[acao.status]} tamanho="sm">
          {ROTULO_STATUS[acao.status]}
        </Etiqueta>
      ),
  },
  {
    chave: 'motivo',
    cabecalho: 'Motivo',
    classe: 'min-w-[16rem]',
    ocultarAte: 'lg',
    celula: (acao) => <span className="text-sm text-conteudo-suave">{acao.motivo}</span>,
  },
  {
    chave: 'resultado',
    cabecalho: 'Resultado depois',
    alinhamento: 'direita',
    ocultarAte: 'xl',
    celula: (acao) =>
      acao.resultadoPosterior === null ? (
        <Dica texto="A avaliação automática do efeito de cada otimização é a próxima etapa. Por enquanto, compare os períodos na visão geral.">
          <span className="text-conteudo-fraco">—</span>
        </Dica>
      ) : (
        <span className="text-sm tabular-nums">{JSON.stringify(acao.resultadoPosterior)}</span>
      ),
  },
]

function CartaoAcao({ acao }: { readonly acao: AcaoOtimizacaoResposta }) {
  return (
    <div className="space-y-2 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-conteudo">{acao.nomeAlvo}</p>
          <p className="text-xs text-conteudo-fraco">
            {ROTULO_NIVEL[acao.nivel]} · {acao.contaNome}
          </p>
        </div>
        <Etiqueta tom={TOM_STATUS[acao.status]} tamanho="sm">
          {ROTULO_STATUS[acao.status]}
        </Etiqueta>
      </div>

      <p className="text-sm">
        <span className="font-medium">{ROTULO_ACAO[acao.acao]}</span>
        <span className="text-conteudo-suave"> · {descreverMudanca(acao)}</span>
      </p>

      <p className="text-sm text-conteudo-suave">{acao.motivo}</p>

      {acao.erroMensagem !== null ? (
        <p className="text-xs text-perigo">{acao.erroMensagem}</p>
      ) : null}

      <p className="text-xs text-conteudo-fraco">
        {formatarDataHora(acao.solicitadaEm)} · {acao.usuarioNome}
      </p>
    </div>
  )
}

const ROTULO_NIVEL: Readonly<Record<AcaoOtimizacaoResposta['nivel'], string>> = {
  CONTA: 'Conta',
  CAMPANHA: 'Campanha',
  CONJUNTO: 'Conjunto',
  ANUNCIO: 'Anúncio',
}

const ROTULO_ACAO: Readonly<Record<AcaoOtimizacaoResposta['acao'], string>> = {
  PAUSAR_ANUNCIO: 'Pausar anúncio',
  ATIVAR_ANUNCIO: 'Ativar anúncio',
  PAUSAR_CONJUNTO: 'Pausar conjunto',
  ATIVAR_CONJUNTO: 'Ativar conjunto',
  PAUSAR_CAMPANHA: 'Pausar campanha',
  ATIVAR_CAMPANHA: 'Ativar campanha',
  ALTERAR_ORCAMENTO: 'Alterar orçamento',
  CRIAR_VARIACAO: 'Criar variação',
  DUPLICAR_ANUNCIO: 'Duplicar anúncio',
}

const ROTULO_STATUS: Readonly<Record<AcaoOtimizacaoResposta['status'], string>> = {
  PENDENTE: 'Pendente',
  EXECUTADA: 'Executada',
  FALHOU: 'Falhou',
  REVERTIDA: 'Revertida',
}

const TOM_STATUS = {
  PENDENTE: 'atencao',
  EXECUTADA: 'sucesso',
  FALHOU: 'perigo',
  REVERTIDA: 'neutro',
} as const

/**
 * "Antes → Depois" legível.
 *
 * Os dois lados vêm de JSONB, e a formatação depende da ação: orçamento é dinheiro, status é
 * rótulo. Um `JSON.stringify` genérico mostraria `{"orcamentoDiario":120}` — tecnicamente
 * correto e ilegível para quem precisa auditar a decisão.
 */
function descreverMudanca(acao: AcaoOtimizacaoResposta): string {
  const anterior = acao.valorAnterior
  const novo = acao.valorNovo

  const orcamentoAnterior = lerNumero(anterior?.orcamentoDiario)
  const orcamentoNovo = lerNumero(novo?.orcamentoDiario)

  if (orcamentoAnterior !== null || orcamentoNovo !== null) {
    return `${formatarMetrica(orcamentoAnterior, 'MOEDA')} → ${formatarMetrica(orcamentoNovo, 'MOEDA')}`
  }

  const statusAnterior = lerTexto(anterior?.status)
  const statusNovo = lerTexto(novo?.status)

  if (statusAnterior !== null || statusNovo !== null) {
    return `${statusAnterior ?? '—'} → ${statusNovo ?? '—'}`
  }

  return '—'
}

function lerNumero(valor: unknown): number | null {
  return typeof valor === 'number' && Number.isFinite(valor) ? valor : null
}

function lerTexto(valor: unknown): string | null {
  return typeof valor === 'string' && valor.length > 0 ? valor : null
}
